import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../api/apiClient';

const DEFAULT_ENVIRONMENT = {
  environment: 'unknown',
  isDev: false,
};

const EnvironmentDataContext = createContext(null);

export function useEnvironment() {
  const context = useContext(EnvironmentDataContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentDataProvider');
  }
  return context;
}

export function EnvironmentDataProvider({ children }) {
  const [environmentData, setEnvironmentData] = useState(DEFAULT_ENVIRONMENT);
  const [isEnvironmentLoading, setIsEnvironmentLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const data = await apiFetch('/api/environment');
        if (isMounted && data && typeof data === 'object') {
          setEnvironmentData({
            environment: data.environment ?? DEFAULT_ENVIRONMENT.environment,
            isDev: Boolean(data.isDev),
          });
        }
      } catch {
        if (isMounted) {
          setEnvironmentData(DEFAULT_ENVIRONMENT);
        }
      } finally {
        if (isMounted) {
          setIsEnvironmentLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      ...environmentData,
      isEnvironmentLoading,
    }),
    [environmentData, isEnvironmentLoading],
  );

  return (
    <EnvironmentDataContext.Provider value={value}>
      {children}
    </EnvironmentDataContext.Provider>
  );
}
